import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * WebRTC Hook for P2P Voice/Video Calls
 *
 * Uses public STUN servers for NAT traversal
 * Falls back to TURN if direct connection fails
 */

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended' | 'failed'
export type CallType = 'audio' | 'video'

interface CallSession {
  recipientId: string
  type: CallType
  startTime?: Date
  endTime?: Date
}

// Fallback STUN servers (used if API fetch fails)
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
]

// Fetch ICE servers from API (includes TURN with credentials)
async function fetchIceServers(): Promise<RTCIceServer[]> {
  try {
    const API_URL = import.meta.env.VITE_API_URL || 'https://api.lynkstar.app/api/v1'
    const token = localStorage.getItem('lynkstar_token')
    const response = await fetch(`${API_URL}/rtc/config`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      console.warn('[WebRTC] Failed to fetch ICE servers, using fallback')
      return FALLBACK_ICE_SERVERS
    }
    const data = await response.json()
    console.log('[WebRTC] ICE servers fetched:', data.iceServers?.length || 0, 'servers')
    return data.iceServers || FALLBACK_ICE_SERVERS
  } catch (err) {
    console.warn('[WebRTC] Error fetching ICE servers:', err)
    return FALLBACK_ICE_SERVERS
  }
}

export function useWebRTC(
  recipientId: string,
  onSignalingMessage?: (message: SignalingMessage) => void
) {
  const [callState, setCallState] = useState<CallState>('idle')
  const [callType, setCallType] = useState<CallType>('audio')
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOff, setIsVideoOff] = useState(false)
  const [callDuration, setCallDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const callSessionRef = useRef<CallSession | null>(null)
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const iceServersRef = useRef<RTCIceServer[]>(FALLBACK_ICE_SERVERS)

  // Initialize peer connection
  const createPeerConnection = useCallback((iceServers?: RTCIceServer[]) => {
    const pc = new RTCPeerConnection({
      iceServers: iceServers || iceServersRef.current,
    })

    pc.onicecandidate = (event) => {
      if (event.candidate && onSignalingMessage) {
        onSignalingMessage({
          type: 'ice-candidate',
          candidate: event.candidate,
          recipientId,
        })
      }
    }

    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE connection state:', pc.iceConnectionState)
      if (pc.iceConnectionState === 'connected') {
        setCallState('connected')
        startCallTimer()
      } else if (pc.iceConnectionState === 'failed') {
        setCallState('failed')
        setError('Connection failed. Please try again.')
        cleanup()
      } else if (pc.iceConnectionState === 'disconnected') {
        setCallState('ended')
        cleanup()
      }
    }

    pc.ontrack = (event) => {
      console.log('[WebRTC] Received remote track')
      setRemoteStream(event.streams[0])
    }

    peerConnectionRef.current = pc
    return pc
  }, [recipientId, onSignalingMessage])

  // Start call timer
  const startCallTimer = useCallback(() => {
    if (callTimerRef.current) return
    callTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1)
    }, 1000)
  }, [])

  // Stop call timer
  const stopCallTimer = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current)
      callTimerRef.current = null
    }
  }, [])

  // Get user media with timeout
  const getUserMedia = useCallback(async (type: CallType): Promise<MediaStream> => {
    const constraints: MediaStreamConstraints = {
      audio: true,
      video: type === 'video',
    }

    console.log('[WebRTC] Requesting media with constraints:', constraints)

    // Check if mediaDevices is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('[WebRTC] mediaDevices not available - need HTTPS')
      throw new Error('媒體設備不可用，請確保使用 HTTPS')
    }

    // Check existing permission state if available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName })
        console.log('[WebRTC] Microphone permission state:', micPermission.state)
      } catch {
        console.log('[WebRTC] Cannot query permission state')
      }
    }

    try {
      // Add timeout to getUserMedia
      const mediaPromise = navigator.mediaDevices.getUserMedia(constraints)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('getUserMedia timeout - 請允許麥克風權限')), 15000)
      })

      console.log('[WebRTC] Waiting for getUserMedia (15s timeout)...')
      const stream = await Promise.race([mediaPromise, timeoutPromise])
      console.log('[WebRTC] Got media stream, tracks:', stream.getTracks().map(t => `${t.kind}:${t.label}`))
      setLocalStream(stream)
      return stream
    } catch (err) {
      console.error('[WebRTC] Failed to get user media:', err)
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          throw new Error('麥克風權限被拒絕，請在瀏覽器設置中允許')
        } else if (err.name === 'NotFoundError') {
          throw new Error('找不到麥克風設備')
        } else if (err.message.includes('timeout')) {
          throw err
        }
      }
      throw new Error('無法訪問麥克風/相機')
    }
  }, [])

  // Start outgoing call
  const startCall = useCallback(async (type: CallType) => {
    try {
      console.log('[WebRTC] startCall called with type:', type, 'recipientId:', recipientId)
      setCallType(type)
      setCallState('calling')
      setError(null)
      setCallDuration(0)

      callSessionRef.current = {
        recipientId,
        type,
        startTime: new Date(),
      }

      // Fetch ICE servers from API
      console.log('[WebRTC] Fetching ICE servers...')
      const iceServers = await fetchIceServers()
      iceServersRef.current = iceServers

      // Get local media
      console.log('[WebRTC] Getting user media...')
      const stream = await getUserMedia(type)
      console.log('[WebRTC] Got user media, tracks:', stream.getTracks().length)

      // Create peer connection directly (not using the callback to avoid closure issues)
      console.log('[WebRTC] Creating peer connection directly...')
      const pc = new RTCPeerConnection({ iceServers })

      // Set up event handlers
      pc.onicecandidate = (event) => {
        if (event.candidate && onSignalingMessage) {
          onSignalingMessage({
            type: 'ice-candidate',
            candidate: event.candidate,
            recipientId,
          })
        }
      }

      pc.oniceconnectionstatechange = () => {
        console.log('[WebRTC] ICE connection state:', pc.iceConnectionState)
        if (pc.iceConnectionState === 'connected') {
          setCallState('connected')
          startCallTimer()
        } else if (pc.iceConnectionState === 'failed') {
          setCallState('failed')
          setError('Connection failed. Please try again.')
        }
      }

      pc.ontrack = (event) => {
        console.log('[WebRTC] Received remote track')
        setRemoteStream(event.streams[0])
      }

      // Store ref AFTER setting up handlers
      peerConnectionRef.current = pc
      console.log('[WebRTC] Peer connection created, signalingState:', pc.signalingState)

      // Add tracks directly with addTrack (simpler approach)
      console.log('[WebRTC] Adding tracks with addTrack...')
      stream.getTracks().forEach((track) => {
        console.log('[WebRTC] Adding track:', track.kind)
        pc.addTrack(track, stream)
      })
      console.log('[WebRTC] Tracks added, senders:', pc.getSenders().length)

      // Verify PC is still open
      if (pc.signalingState === 'closed') {
        throw new Error('Peer connection was closed unexpectedly')
      }

      // Create offer immediately (no delay)
      console.log('[WebRTC] Creating offer, signalingState:', pc.signalingState)
      let offer: RTCSessionDescriptionInit
      try {
        const startTime = Date.now()
        offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: type === 'video'
        })
        const elapsed = Date.now() - startTime
        console.log('[WebRTC] Offer created in', elapsed, 'ms, SDP length:', offer.sdp?.length || 0)
      } catch (offerError) {
        console.error('[WebRTC] createOffer failed:', offerError)
        throw offerError
      }

      console.log('[WebRTC] Setting local description...')
      await pc.setLocalDescription(offer)
      console.log('[WebRTC] Local description set, signalingState:', pc.signalingState)

      // Send offer via signaling
      console.log('[WebRTC] onSignalingMessage exists:', !!onSignalingMessage)
      if (onSignalingMessage) {
        console.log('[WebRTC] Sending offer via signaling callback')
        onSignalingMessage({
          type: 'offer',
          sdp: offer,
          callType: type,
          recipientId,
        })
      } else {
        console.error('[WebRTC] No onSignalingMessage callback!')
      }
    } catch (err) {
      console.error('[WebRTC] Failed to start call:', err)
      setCallState('failed')
      setError(err instanceof Error ? err.message : 'Failed to start call')
      cleanup()
    }
  }, [recipientId, getUserMedia, createPeerConnection, onSignalingMessage])

  // Answer incoming call
  const answerCall = useCallback(async (offer: RTCSessionDescriptionInit, type: CallType) => {
    try {
      setCallType(type)
      setCallState('connected')
      setError(null)
      setCallDuration(0)

      callSessionRef.current = {
        recipientId,
        type,
        startTime: new Date(),
      }

      // Fetch ICE servers from API
      const iceServers = await fetchIceServers()
      iceServersRef.current = iceServers

      // Get local media
      const stream = await getUserMedia(type)

      // Create peer connection with fetched ICE servers
      const pc = createPeerConnection(iceServers)

      // Add tracks to peer connection
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream)
      })

      // Set remote description
      await pc.setRemoteDescription(offer)

      // Create answer
      const answer = await pc.createAnswer()
      await pc.setLocalDescription(answer)

      // Send answer via signaling
      if (onSignalingMessage) {
        onSignalingMessage({
          type: 'answer',
          sdp: answer,
          recipientId,
        })
      }
    } catch (err) {
      console.error('[WebRTC] Failed to answer call:', err)
      setCallState('failed')
      setError(err instanceof Error ? err.message : 'Failed to answer call')
      cleanup()
    }
  }, [recipientId, getUserMedia, createPeerConnection, onSignalingMessage])

  // Handle received answer
  const handleAnswer = useCallback(async (answer: RTCSessionDescriptionInit) => {
    const pc = peerConnectionRef.current
    if (!pc) return

    try {
      await pc.setRemoteDescription(answer)
    } catch (err) {
      console.error('[WebRTC] Failed to set remote description:', err)
    }
  }, [])

  // Handle ICE candidate
  const handleIceCandidate = useCallback(async (candidate: RTCIceCandidateInit) => {
    const pc = peerConnectionRef.current
    if (!pc) return

    try {
      await pc.addIceCandidate(candidate)
    } catch (err) {
      console.error('[WebRTC] Failed to add ICE candidate:', err)
    }
  }, [])

  // End call
  const endCall = useCallback(() => {
    if (onSignalingMessage) {
      onSignalingMessage({
        type: 'end-call',
        recipientId,
      })
    }

    setCallState('ended')
    cleanup()
  }, [recipientId, onSignalingMessage])

  // Reject call
  const rejectCall = useCallback(() => {
    if (onSignalingMessage) {
      onSignalingMessage({
        type: 'reject-call',
        recipientId,
      })
    }

    setCallState('idle')
    cleanup()
  }, [recipientId, onSignalingMessage])

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled
      })
      setIsMuted((prev) => !prev)
    }
  }, [localStream])

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStream) {
      localStream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled
      })
      setIsVideoOff((prev) => !prev)
    }
  }, [localStream])

  // Switch camera (for mobile)
  const switchCamera = useCallback(async () => {
    if (!localStream || callType !== 'video') return

    const videoTrack = localStream.getVideoTracks()[0]
    if (!videoTrack) return

    try {
      // Get current facing mode
      const currentFacingMode = videoTrack.getSettings().facingMode
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user'

      // Get new stream with different camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: false,
      })

      const newVideoTrack = newStream.getVideoTracks()[0]

      // Replace track in peer connection
      const pc = peerConnectionRef.current
      if (pc) {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video')
        if (sender) {
          await sender.replaceTrack(newVideoTrack)
        }
      }

      // Replace track in local stream
      localStream.removeTrack(videoTrack)
      localStream.addTrack(newVideoTrack)
      videoTrack.stop()
    } catch (err) {
      console.error('[WebRTC] Failed to switch camera:', err)
    }
  }, [localStream, callType])

  // Cleanup
  const cleanup = useCallback(() => {
    stopCallTimer()

    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop())
      setLocalStream(null)
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }

    setRemoteStream(null)
    setIsMuted(false)
    setIsVideoOff(false)

    if (callSessionRef.current) {
      callSessionRef.current.endTime = new Date()
    }
  }, [localStream, stopCallTimer])

  // Format call duration
  const formatDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    callState,
    callType,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    callDuration,
    formattedDuration: formatDuration(callDuration),
    error,
    startCall,
    answerCall,
    handleAnswer,
    handleIceCandidate,
    endCall,
    rejectCall,
    toggleMute,
    toggleVideo,
    switchCamera,
  }
}

// Signaling message types
export interface SignalingMessage {
  type: 'offer' | 'answer' | 'ice-candidate' | 'end-call' | 'reject-call'
  recipientId: string
  sdp?: RTCSessionDescriptionInit
  candidate?: RTCIceCandidate
  callType?: CallType
}

export type { CallSession }
